import {
  Dispatch,
  Fragment,
  memo,
  ReactElement,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { TypewriterTracking } from '@breathelife/frontend-tracking';
import { hash } from '@breathelife/hash';
import { Box, Divider } from '@breathelife/mui';
import { Application, ESignSigner2FAInfo, ProductsEntity, SignatureType } from '@breathelife/types';
import { ApplicationMode, ApplicationModes, getSectionById, RenderingQuestionnaire } from '@zinnia/form-engine-sdk';

import { Lead } from '../../..';
import { MemoizedAssistedApplicationHeader as AssistedApplicationHeader } from '../../../Components/AssistedApplication/AssistedApplicationView/AssistedApplicationHeader';
import {
  Direction,
  findSectionGroup,
  getSectionGroupWithIndicesId,
  SectionGroupIndices,
  updateVisitedSectionIndices,
} from '../../../Helpers/assistedApplication/sectionGroups';
import { getApplicantFullName, isInsuredPeopleSectionGroup } from '../../../Helpers/questionnaireAnswers';
import { useAAQuestionnaireVersionContext, useCarrierContext } from '../../../Hooks';
import {
  useAllSections,
  useSectionNavigation,
  useSectionViewPresets,
} from '../../../Hooks/Application/useSectionSelection';
import { useAssistedApplicationContext } from '../../../Hooks/useAssistedApplicationContext';
import { useQuestionnaireEngine } from '../../../Hooks/useQuestionnaireEngine';
import { useFullPageLoaderContext } from '../../LoadingView/FullPageLoader';
import { SkipLink } from '../../SkipLink/SkipLink';
import { NumberOfVisibleErrorsBySectionGroup } from '../AssistedApplicationContainer';
import { AssistedApplicationLayout } from '../AssistedApplicationLayout/AssistedApplicationLayout';
import { DeleteProposedInsuredModal } from '../Modals/ProposedInsured/DeleteProposedInsuredModal';
import { unsetCurrentApplication } from '../Subscribers/saveAnswersSubscriber';
import { resetStoredSectionState } from '../useStoredSectionState';
import { ApplicationDetailsWidgetsContainer } from './ApplicationDetailsWidgets/ApplicationDetailsWidgetsContainer';
import { ApplicationDetailsWidgetsStickyWrapper } from './ApplicationDetailsWidgets/ApplicationDetailsWidgetsStickyWrapper';
import { SectionView } from './AssistedApplicationForm/SectionView';
import { AssistedApplicationNavigation } from './AssistedApplicationNavigation';
import { NavigationButton } from './NavigationButton';
import { ProposedInsuredTabBar } from './ProposedInsuredTabBar/ProposedInsuredTabBar';
import { FormContainer, StyledNextNavigationButton } from './Styles';

type Props = {
  renderingQuestionnaire: RenderingQuestionnaire;
  selectedProductId: string;
  isApplicationSubmitted: boolean;
  isApplicationSigned: boolean;
  applicationMode: ApplicationMode;
  onOpenSubmissionDetailsModal: () => void;
  productsEntity: ProductsEntity<string> | undefined;
  proposedInsuredIndexToDelete: number;
  onOpenDeleteProposedInsuredModal: (index: number) => void;
  onCloseDeleteProposedInsuredModal: () => void;
  applicationSignatureType?: SignatureType;
  isReadOnly?: boolean;
  visitedSectionIndicesByGroup: SectionGroupIndices[] | undefined;
  setVisitedSectionsIndicesByGroup: Dispatch<SetStateAction<SectionGroupIndices[] | undefined>>;
  numberOfVisibleErrorsBySectionGroup: NumberOfVisibleErrorsBySectionGroup;
  closeAssistedApplication: () => void;
  application: Application;
  onOpenSubmitApplicationModal: () => void;
  onOpenFileAttachmentModal: () => void;
  triggerIdentityVerification: () => void;
  onOpenDocumentDrawerHook: () => void;
  onOpenSubmitPaperAppModal: () => void;
  onOpenInstantIdReportDrawer: () => void;
  processSubmission: (signers?: ESignSigner2FAInfo[], inPerson?: boolean) => Promise<void>;
  isSubmitButtonDisabled: boolean;
  lead?: Lead;
  setOpenESignatureDrawerRequested: (value: boolean) => void;
  isApplicantMinor: boolean;
  showCompletionIcons: boolean;
};

function AssistedApplicationView(props: Props): ReactElement | null {
  const { t } = useTranslation();
  const { questionnaireEngine } = useQuestionnaireEngine();
  const {
    renderingQuestionnaire,
    selectedProductId,
    isApplicationSubmitted,
    isApplicationSigned,
    isReadOnly,
    onOpenSubmissionDetailsModal,
    productsEntity,
    proposedInsuredIndexToDelete,
    onOpenDeleteProposedInsuredModal,
    onCloseDeleteProposedInsuredModal,
    applicationMode,
    visitedSectionIndicesByGroup,
    setVisitedSectionsIndicesByGroup,
    numberOfVisibleErrorsBySectionGroup,
    application,
    closeAssistedApplication,
    applicationSignatureType,
    onOpenSubmitApplicationModal,
    onOpenFileAttachmentModal,
    triggerIdentityVerification,
    onOpenDocumentDrawerHook,
    onOpenSubmitPaperAppModal,
    processSubmission,
    isSubmitButtonDisabled,
    lead,
    setOpenESignatureDrawerRequested,
    isApplicantMinor,
    onOpenInstantIdReportDrawer,
    showCompletionIcons,
  } = props;

  const {
    currentProposedInsuredIndex,
    currentSectionGroupId,
    proposedInsuredTabs,
    onSelectProposedInsured,
    onSwitchSectionGroupTab,
    activeSectionIndex,
    onSetActiveSectionIndex,
  } = useAssistedApplicationContext();

  async function onOpenESignatureDrawer(): Promise<void> {
    !application.submitted;
    setOpenESignatureDrawerRequested(true);
  }

  const { features } = useCarrierContext();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { closeFullPageLoader, isOpen: fullPageLoaderIsOpen } = useFullPageLoaderContext();
  const [isFirstRender, setIsFirstRender] = useState<boolean>(true);

  const sectionGroup =
    findSectionGroup(renderingQuestionnaire, currentSectionGroupId, currentProposedInsuredIndex) ??
    renderingQuestionnaire[0];

  const sectionContainerRef = useRef<HTMLDivElement>(null);

  const allSections = useAllSections({ sectionGroup, visitedSectionIndicesByGroup });

  useSectionViewPresets(allSections[activeSectionIndex]);

  const { nextSectionData, previousSectionData } = useSectionNavigation(renderingQuestionnaire);

  const scrollToTop = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo(0, 0);
    }
  }, [scrollContainerRef]);

  const navigateToSection = useCallback(
    (nextSectionIndex: number, direction?: Direction): void => {
      // Do nothing if user clicks on the current displayed section.
      if (nextSectionIndex === activeSectionIndex) return;

      const surrogateId = proposedInsuredTabs[currentProposedInsuredIndex]?.surrogateId ?? '';

      let sectionIndicesByGroupId = getSectionGroupWithIndicesId(currentSectionGroupId, surrogateId);

      updateVisitedSectionIndices({
        activeSectionIndex,
        sectionIndicesByGroupId,
        setVisitedSectionsIndicesByGroup,
        visitedSectionIndicesByGroup,
      });

      if (direction) {
        const section = direction === Direction.previous ? previousSectionData : nextSectionData;
        const groupId = section?.groupId ?? currentSectionGroupId;
        const proposedInsuredIndex = section?.proposedInsuredIndex ?? currentProposedInsuredIndex;
        onSwitchSectionGroupTab(groupId);
        onSelectProposedInsured(proposedInsuredIndex);
        sectionIndicesByGroupId = getSectionGroupWithIndicesId(groupId, surrogateId);
      }

      const nextSectionGroup = visitedSectionIndicesByGroup?.find(
        (sectionGroup) => sectionGroup.id === sectionIndicesByGroupId,
      );

      // Show errors if the next section has already been visited before, hide them otherwise
      questionnaireEngine.setDisplayError(nextSectionGroup?.indices.includes(nextSectionIndex) || false);

      // Scroll to top of the scrollable content when navigating to a different section
      scrollToTop();

      // Activate the next section
      onSetActiveSectionIndex(nextSectionIndex);
    },
    [
      activeSectionIndex,
      currentProposedInsuredIndex,
      currentSectionGroupId,
      nextSectionData,
      onSelectProposedInsured,
      onSwitchSectionGroupTab,
      previousSectionData,
      proposedInsuredTabs,
      visitedSectionIndicesByGroup,
      onSetActiveSectionIndex,
      questionnaireEngine,
      scrollToTop,
      setVisitedSectionsIndicesByGroup,
    ],
  );

  // Check for the completion of the question to render prompt to lock the application
  const { maxNumberOfInsured } = useAAQuestionnaireVersionContext();

  // ------------------------------------

  const section = useMemo(() => {
    const activeSection = getSectionById(sectionGroup, allSections[activeSectionIndex]?.id);
    if (!activeSection) {
      onSetActiveSectionIndex(0);
      return allSections[0];
    }
    return activeSection;
  }, [sectionGroup, allSections, activeSectionIndex, onSetActiveSectionIndex]);

  const onSubmitApplication = useCallback(async () => {
    if (
      !application ||
      !applicationSignatureType ||
      (!application.isPreview && applicationSignatureType === SignatureType.eSignature)
    )
      return;

    onOpenSubmitApplicationModal();
    return processSubmission();
  }, [application, applicationSignatureType, onOpenSubmitApplicationModal, processSubmission]);

  const hasNoSignature =
    application.mode !== ApplicationModes.paper &&
    applicationSignatureType !== SignatureType.eSignature &&
    !application.signed;

  const isSubmitButtonVisible = !application.submitted || hasNoSignature;

  const onOpenDocumentsDrawer = useCallback(() => {
    TypewriterTracking.clickedButton({
      buttonName: 'documents drawer',
      hashedId: application?.id ? hash(application.id) : null,
    });
    onOpenDocumentDrawerHook();
  }, [application, onOpenDocumentDrawerHook]);

  useEffect(() => {
    if (section && fullPageLoaderIsOpen && isFirstRender) {
      closeFullPageLoader();
      setIsFirstRender(false);
    }
  }, [section, fullPageLoaderIsOpen, closeFullPageLoader, isFirstRender]);

  const applicantName = useMemo(() => {
    return getApplicantFullName(questionnaireEngine, application.lineOfBusiness, features);
  }, [renderingQuestionnaire, questionnaireEngine, application.lineOfBusiness, features]);

  if (!section) return null;

  const withInsuredTabs = maxNumberOfInsured > 1 && isInsuredPeopleSectionGroup(sectionGroup);
  const skipToAssistedApplicationFormId = 'assistedApplicationForm';

  return (
    <Fragment>
      <AssistedApplicationLayout
        scrollContainerRef={scrollContainerRef}
        onClose={() => {
          unsetCurrentApplication();
          closeAssistedApplication();
          resetStoredSectionState();
        }}
      >
        <AssistedApplicationLayout.Header>
          <AssistedApplicationHeader
            applicantName={applicantName}
            isApplicantMinor={isApplicantMinor}
            applicationSignatureType={applicationSignatureType}
            applicationMode={application.mode}
            isPreview={application.isPreview}
            isSubmitButtonDisabled={isSubmitButtonDisabled}
            isSubmitButtonVisible={isSubmitButtonVisible}
            isLeadArchived={!!lead?.archived}
            onSubmitApplication={onSubmitApplication}
            onOpenESignatureDetails={onOpenESignatureDrawer}
            onOpenFileAttachmentModal={onOpenFileAttachmentModal}
            onOpenDocumentsDrawer={onOpenDocumentsDrawer}
            triggerIdentityVerification={triggerIdentityVerification}
            onOpenInstantIdReportDrawer={onOpenInstantIdReportDrawer}
            onOpenSubmitPaperAppModal={onOpenSubmitPaperAppModal}
            numberOfVisibleErrorsBySectionGroup={numberOfVisibleErrorsBySectionGroup}
            showCompletionIcons={application.mode !== ApplicationModes.paper}
            onScrollToTop={scrollToTop}
          />
        </AssistedApplicationLayout.Header>

        {withInsuredTabs && (
          <AssistedApplicationLayout.Subheader>
            <ProposedInsuredTabBar
              maxTabs={maxNumberOfInsured}
              onOpenDeleteProposedInsuredModal={onOpenDeleteProposedInsuredModal}
              showCompletionIcons={showCompletionIcons}
            />
            <DeleteProposedInsuredModal
              isOpen={proposedInsuredIndexToDelete !== -1}
              onClose={onCloseDeleteProposedInsuredModal}
              proposedInsuredIndexToDelete={proposedInsuredIndexToDelete}
            />
          </AssistedApplicationLayout.Subheader>
        )}

        <AssistedApplicationLayout.Sidebar>
          <SkipLink href={`#${skipToAssistedApplicationFormId}`} title={t('skipLinks.navigation')} />
          <AssistedApplicationNavigation
            scrollContainerRef={scrollContainerRef}
            activeSectionIndex={activeSectionIndex}
            onSectionClick={navigateToSection}
            hasSectionEndIcons={applicationMode !== ApplicationModes.paper && !isReadOnly}
            allSections={allSections}
          />
        </AssistedApplicationLayout.Sidebar>

        <AssistedApplicationLayout.ScrollableContent>
          <FormContainer
            id={skipToAssistedApplicationFormId}
            flex={2}
            mx={8}
            {...{ ref: sectionContainerRef }}
            role='form'
          >
            <SectionView section={section} />
            <Divider />
            <Box display='flex' py={3}>
              {previousSectionData?.section && (
                <NavigationButton
                  direction={Direction.previous}
                  onClick={() => navigateToSection(previousSectionData.index, Direction.previous)}
                >
                  {previousSectionData.section.title}
                </NavigationButton>
              )}
              {nextSectionData?.section && (
                <StyledNextNavigationButton
                  direction={Direction.next}
                  onClick={() => navigateToSection(nextSectionData.index, Direction.next)}
                >
                  {nextSectionData.section.title}
                </StyledNextNavigationButton>
              )}
            </Box>
          </FormContainer>
          <ApplicationDetailsWidgetsStickyWrapper flex={1} mt={5} role='region'>
            <ApplicationDetailsWidgetsContainer
              selectedProductId={selectedProductId}
              isApplicationSubmitted={isApplicationSubmitted}
              isApplicationSigned={isApplicationSigned}
              onOpenSubmissionDetailsModal={onOpenSubmissionDetailsModal}
              productsEntity={productsEntity}
            />
          </ApplicationDetailsWidgetsStickyWrapper>
        </AssistedApplicationLayout.ScrollableContent>
      </AssistedApplicationLayout>
    </Fragment>
  );
}

export const MemoizedAssistedApplicationView = memo(AssistedApplicationView);
